import React, { useState, useMemo, useEffect } from 'react'
import {
  Input,
  Button,
  Select,
  Collapse,
  Checkbox,
  Radio,
  Space,
  Tag,
  Divider,
  Typography,
  theme,
  Badge,
  Slider,
  Card,
  Tooltip,
  Row,
  Col,
  Affix
} from 'antd'
import {
  SearchOutlined,
  CaretRightOutlined,
  CloseOutlined,
  ClearOutlined,
  FilterOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  GlobalOutlined,
  UserOutlined,
  VideoCameraOutlined,
  HomeOutlined,
  CalendarOutlined,
  EnvironmentOutlined
} from '@ant-design/icons'
import { readProfessionsSpecialties, readSearchTypes } from '../../api/searchApi'
import GoogleAddressMobile from '../../../reusable-components/GoogleAddressMobile'
import { useSelector, useDispatch } from 'react-redux'
import { geocodeByAddress } from 'react-google-places-autocomplete'
import { setSearchResults } from '../../utils/searchReducer'

const { Option } = Select
const { Panel } = Collapse
const { Title, Text } = Typography

const SearchRefinementMobile = ({
  onSearch,
  onFilterChange,
  searchFilters = {},
  loading = false,
  searchType,
  setSearchType,
  onClose,
  isFullScreen = false,
  hasSearched = false,
  isVirtualVisit = false
}) => {
  const [searchTypes, setSearchTypes] = useState([])
  const [address, setAddress] = useState(searchFilters.address || '')
  const [placeId, setPlaceId] = useState(searchFilters.placeId || '')
  const [location, setLocation] = useState(searchFilters.location || null)
  const [maxDistance, setMaxDistance] = useState(searchFilters.maxDistance || 25)
  const [isLoadingLocation, setIsLoadingLocation] = useState(false)
  const [hasManuallySetLocation, setHasManuallySetLocation] = useState(false)
  const [loadingStartTime, setLoadingStartTime] = useState(null)
  const [isUILoading, setIsUILoading] = useState(false)
  const { token } = theme.useToken()
  const dispatch = useDispatch()
  const patientAddress = useSelector((state) => state.auth.agiliteUser.residentialAddress)

  useEffect(() => {
    fetchProfessionsSpecialties()
  }, [])

  // Track loading state with minimum animation time
  useEffect(() => {
    if (loading) {
      setLoadingStartTime(Date.now())
      setIsUILoading(true)
    } else if (loadingStartTime) {
      const elapsed = Date.now() - loadingStartTime
      const minimumLoadingTime = 1000 // Reduced for mobile

      if (elapsed < minimumLoadingTime) {
        const remainingTime = minimumLoadingTime - elapsed
        setTimeout(() => {
          setIsUILoading(false)
        }, remainingTime)
      } else {
        setIsUILoading(false)
      }
    }
  }, [loading, loadingStartTime])

  // Auto-set patient address as default location
  useEffect(() => {
    const setPatientLocation = async () => {
      if (patientAddress && !location && !hasManuallySetLocation && !isLoadingLocation) {
        setIsLoadingLocation(true)
        try {
          const results = await geocodeByAddress(patientAddress)

          if (results && results.length > 0) {
            const result = results[0]
            const newLocation = {
              address: patientAddress,
              place_id: result.place_id
            }

            setAddress(patientAddress)
            setPlaceId(result.place_id)
            setLocation(newLocation)

            updateLocationInRedux({
              address: patientAddress,
              placeId: result.place_id,
              location: newLocation
            })
          }
        } catch (error) {
          console.error('Error geocoding patient address:', error)
        } finally {
          setIsLoadingLocation(false)
        }
      }
    }

    setPatientLocation()
  }, [patientAddress, location, hasManuallySetLocation, isLoadingLocation])

  // Set virtual consultation by default when isVirtualVisit is true
  useEffect(() => {
    if (isVirtualVisit && (!searchFilters.consultType || searchFilters.consultType.length === 0)) {
      onFilterChange?.('consultType', ['virtual'])
    }
  }, [isVirtualVisit])

  // Set searchType to virtual_consultation when in Virtual Visit mode
  useEffect(() => {
    if (isVirtualVisit && searchType !== 'virtual_consultation') {
      setSearchType('virtual_consultation')
    }
  }, [isVirtualVisit, searchType, setSearchType])

  const fetchProfessionsSpecialties = async () => {
    try {
      const tmpSearchTypes = await readSearchTypes()
      setSearchTypes(tmpSearchTypes)
    } catch (error) {}
  }

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0
    if (searchFilters.consultType?.length > 0) count += searchFilters.consultType.length
    if (searchFilters.timeSlot) count += 1
    if (searchFilters.languages?.length > 0) count += searchFilters.languages.length
    if (searchFilters.gender) count += 1
    return count
  }, [searchFilters])

  const handleNewSearch = () => {
    const searchParams = {
      ...searchFilters,
      searchType,
      address,
      placeId,
      location,
      maxDistance
    }

    onSearch?.(searchParams)
  }

  const handleFilterChange = (filterType, value) => {
    onFilterChange?.(filterType, value)
  }

  const handleSearchTypeChange = (value) => {
    onFilterChange?.('searchType', value)

    const searchParams = {
      ...searchFilters,
      searchType: value,
      address,
      placeId,
      location,
      maxDistance
    }

    onSearch?.(searchParams)
  }

  const handleLocationChange = (value) => {
    setHasManuallySetLocation(true)
    if (value && value.value) {
      const newPlaceId = value.value.place_id
      const newAddress = value.label
      const newLocation = {
        address: newAddress,
        place_id: newPlaceId
      }

      setPlaceId(newPlaceId)
      setAddress(newAddress)
      setLocation(newLocation)

      updateLocationInRedux({
        placeId: newPlaceId,
        address: newAddress,
        location: newLocation
      })
    } else {
      setPlaceId('')
      setAddress('')
      setLocation(null)

      updateLocationInRedux({
        placeId: '',
        address: '',
        location: null
      })
    }
  }

  const clearAllFilters = () => {
    onFilterChange?.('consultType', [])
    onFilterChange?.('timeSlot', '')
    onFilterChange?.('languages', [])
    onFilterChange?.('gender', '')
    onFilterChange?.('searchType', 'general_practitioner')

    setAddress('')
    setPlaceId('')
    setLocation(null)
    setMaxDistance(25)
    setIsLoadingLocation(false)
    setHasManuallySetLocation(false)

    updateLocationInRedux({
      address: '',
      placeId: '',
      location: null,
      maxDistance: 25
    })
  }

  const updateLocationInRedux = (updates) => {
    Object.entries(updates).forEach(([key, value]) => {
      onFilterChange?.(key, value)
    })
  }

  // Synchronize local state with Redux state when it changes
  useEffect(() => {
    if (searchFilters.address !== address) setAddress(searchFilters.address || '')
    if (searchFilters.placeId !== placeId) setPlaceId(searchFilters.placeId || '')
    if (JSON.stringify(searchFilters.location) !== JSON.stringify(location)) {
      setLocation(searchFilters.location || null)
    }
    if (searchFilters.maxDistance !== maxDistance) setMaxDistance(searchFilters.maxDistance || 25)
  }, [
    searchFilters.address,
    searchFilters.placeId,
    searchFilters.location,
    searchFilters.maxDistance,
    address,
    placeId,
    location,
    maxDistance
  ])

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid #f0f0f0',
          background: '#fff',
          position: isFullScreen ? 'sticky' : 'static',
          top: 0,
          zIndex: 100
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={4} style={{ margin: 0, fontSize: '18px' }}>
              {isVirtualVisit
                ? 'Find Virtual Healthcare Professionals'
                : isFullScreen
                ? 'Find Healthcare Professionals'
                : 'Search Filters'}
            </Title>
            {activeFiltersCount > 0 && (
              <Text type='secondary' style={{ fontSize: '14px' }}>
                {activeFiltersCount} filter{activeFiltersCount !== 1 ? 's' : ''} applied
              </Text>
            )}
            {isFullScreen && !hasSearched && (
              <Text type='secondary' style={{ fontSize: '14px', display: 'block', marginTop: '4px' }}>
                {isVirtualVisit
                  ? 'Find doctors available for virtual consultations'
                  : 'Set your preferences to find the right doctor for you'}
              </Text>
            )}
          </div>
          {!isFullScreen && <Button type='text' icon={<CloseOutlined />} onClick={onClose} />}
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }} className='mobile-search-content'>
        {/* Search Type Selector - Only show if not in Virtual Visit mode */}
        {!isVirtualVisit && (
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '12px', fontSize: '16px' }}>
              What are you looking for?
            </Text>
            <Select
              value={searchType}
              onChange={handleSearchTypeChange}
              style={{ width: '100%' }}
              size='large'
              placeholder='Select profession type'
              disabled={isUILoading}
            >
              {searchTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {/* Location */}
        <div style={{ marginBottom: '24px' }}>
          <Text strong style={{ display: 'block', marginBottom: '12px', fontSize: '16px' }}>
            <EnvironmentOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
            Location
          </Text>
          <GoogleAddressMobile
            selectProps={{
              placeholder: address || 'Enter your location',
              value: address ? { label: address, value: { place_id: placeId } } : null,
              onChange: handleLocationChange,
              isDisabled: isLoadingLocation || isUILoading,
              styles: {
                control: (provided) => ({
                  ...provided,
                  minHeight: '48px',
                  fontSize: '16px'
                })
              }
            }}
          />

          {location && (
            <div style={{ marginTop: '16px' }}>
              <Text style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
                Search within: <strong>{maxDistance} km</strong>
              </Text>
              <Slider
                min={1}
                max={50}
                value={maxDistance}
                onChange={(value) => {
                  setMaxDistance(value)
                  updateLocationInRedux({ maxDistance: value })
                }}
                marks={{
                  1: '1km',
                  25: '25km',
                  50: '50km'
                }}
                tooltip={{ formatter: (value) => `${value}km` }}
                disabled={isUILoading}
              />
            </div>
          )}
        </div>

        {/* Type of Consultation - Only show if not in Virtual Visit mode */}
        {!isVirtualVisit && (
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '16px' }}>
              <VideoCameraOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
              Type of Consultation
            </Text>
            <Checkbox.Group
              style={{ width: '100%' }}
              value={searchFilters.consultType || []}
              onChange={(values) => handleFilterChange('consultType', values)}
              disabled={isUILoading}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Checkbox value='inPerson' style={{ fontSize: '16px', padding: '8px 0' }}>
                  <Space>
                    <UserOutlined style={{ color: '#52c41a' }} />
                    <span>In-Person Consultation</span>
                  </Space>
                </Checkbox>
                <Checkbox value='virtual' style={{ fontSize: '16px', padding: '8px 0' }}>
                  <Space>
                    <VideoCameraOutlined style={{ color: '#1890ff' }} />
                    <span>Virtual Consultation</span>
                  </Space>
                </Checkbox>
                <Checkbox value='homeVisit' style={{ fontSize: '16px', padding: '8px 0' }}>
                  <Space>
                    <HomeOutlined style={{ color: '#fa8c16' }} />
                    <span>Home Visit</span>
                  </Space>
                </Checkbox>
              </div>
            </Checkbox.Group>
          </div>
        )}

        {/* Time Availability */}
        <div style={{ marginBottom: '24px' }}>
          <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '16px' }}>
            <ClockCircleOutlined style={{ marginRight: '8px', color: token.colorWarning }} />
            Time Availability
          </Text>
          <Radio.Group
            style={{ width: '100%' }}
            value={searchFilters.timeSlot}
            onChange={(e) => handleFilterChange('timeSlot', e.target.value)}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Radio value='morning' style={{ fontSize: '16px', padding: '8px 0' }}>
                <Space>
                  <span style={{ color: '#fa8c16' }}>🌅</span>
                  <span>Morning (6AM - 12PM)</span>
                </Space>
              </Radio>
              <Radio value='afternoon' style={{ fontSize: '16px', padding: '8px 0' }}>
                <Space>
                  <span style={{ color: '#fadb14' }}>☀️</span>
                  <span>Afternoon (12PM - 6PM)</span>
                </Space>
              </Radio>
              <Radio value='evening' style={{ fontSize: '16px', padding: '8px 0' }}>
                <Space>
                  <span style={{ color: '#1e40af' }}>🌙</span>
                  <span>Evening (6PM - 12AM)</span>
                </Space>
              </Radio>
            </div>
          </Radio.Group>
        </div>

        {/* Languages */}
        <div style={{ marginBottom: '24px' }}>
          <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '16px' }}>
            <GlobalOutlined style={{ marginRight: '8px', color: token.colorSuccess }} />
            Languages
          </Text>
          <Checkbox.Group
            style={{ width: '100%' }}
            value={searchFilters.languages || []}
            onChange={(values) => handleFilterChange('languages', values)}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { value: 'english', label: 'English', flag: '🇬🇧' },
                { value: 'afrikaans', label: 'Afrikaans', flag: '🇿🇦' },
                { value: 'zulu', label: 'Zulu', flag: '🇿🇦' },
                { value: 'xhosa', label: 'Xhosa', flag: '🇿🇦' },
                { value: 'sotho', label: 'Sotho', flag: '🇿🇦' }
              ].map((lang) => (
                <Checkbox key={lang.value} value={lang.value} style={{ fontSize: '16px', padding: '8px 0' }}>
                  <Space>
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                  </Space>
                </Checkbox>
              ))}
            </div>
          </Checkbox.Group>
        </div>

        {/* Doctor Gender */}
        <div style={{ marginBottom: '24px' }}>
          <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '16px' }}>
            <TeamOutlined style={{ marginRight: '8px', color: token.colorInfo }} />
            Doctor Gender
          </Text>
          <Radio.Group
            style={{ width: '100%' }}
            value={searchFilters.gender}
            onChange={(e) => handleFilterChange('gender', e.target.value)}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Radio value='male' style={{ fontSize: '16px', padding: '8px 0' }}>
                <Space>
                  <span style={{ color: '#1890ff' }}>👨‍⚕️</span>
                  <span>Male Doctor</span>
                </Space>
              </Radio>
              <Radio value='female' style={{ fontSize: '16px', padding: '8px 0' }}>
                <Space>
                  <span style={{ color: '#eb2f96' }}>👩‍⚕️</span>
                  <span>Female Doctor</span>
                </Space>
              </Radio>
              <Radio value='any' style={{ fontSize: '16px', padding: '8px 0' }}>
                <Space>
                  <span>👩‍⚕️👨‍⚕️</span>
                  <span>No Preference</span>
                </Space>
              </Radio>
            </div>
          </Radio.Group>
        </div>
      </div>

      {/* Sticky Footer with Actions */}
      <div
        style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          background: '#fff'
        }}
      >
        <Space style={{ width: '100%' }} direction='vertical' size='middle'>
          <Button
            type='primary'
            size='large'
            block
            icon={<SearchOutlined />}
            onClick={handleNewSearch}
            loading={isUILoading}
            style={{ height: '48px', fontSize: '16px' }}
          >
            Search Doctors
          </Button>

          {activeFiltersCount > 0 && (
            <Button
              type='text'
              size='large'
              block
              icon={<ClearOutlined />}
              onClick={clearAllFilters}
              style={{ height: '40px' }}
            >
              Clear All Filters ({activeFiltersCount})
            </Button>
          )}
        </Space>
      </div>
    </div>
  )
}

export default SearchRefinementMobile
